import { useEffect, useRef, useState } from "react";
import { Button, Flex, TextInput } from "@mantine/core";
import { FormRow, FormColumn } from "../components/common/Form/FormRow";
import Container from "../components/common/Container";
import PageHeader from "../components/common/PageHeader";
import { useAuth } from "../context/AuthContext";
import { createEmployee, uploadEmployeeDocument } from "../api/employeeApi";
import { useForm } from "@mantine/form";
import {
  creationNotification,
  errorNotification,
  samePhoneNumberErroNotif,
} from "../utils/useNotification";
import { unwrap } from "../utils/apiResult";
import {
  combineValidators,
  validateMinLength,
  validateNoSpaces,
  validatePhoneNumberIsNumeric,
  validatePhoneNumberStartsWithZero,
  validateRequired,
} from "../utils/validations";
import CustomDatePicker from "../components/common/Inputs/CustomDatePicker";
import { CreateEmployeeParams } from "../types/types";
import EmployeeTagFields from "../components/Employees/EmployeeTagFields";
import { useLocation } from "react-router";
import DocumentUploadCard from "../components/EmployeeDetail/DocumentUploadCard";
import PlusIcon from "../components/common/Icons/PlusIcon";
import { denormalizePhone } from "../utils/phoneUtils";

const NewEmployee = () => {
  const { company } = useAuth();
  const location = useLocation();
  const applicant = location.state?.applicant;
  const formRef = useRef<HTMLFormElement>(null);

  const [documents, setDocuments] = useState<any>();

  useEffect(() => {
    const formattedDocs = company?.documents?.map((doc: any) => ({
      ...doc,
      file: null,
    }));

    setDocuments(formattedDocs);
  }, []);


  const form = useForm<CreateEmployeeParams>({
    initialValues: {
      name: "",
      phone: "",
      departmentIds: [],
      tagIds: [],
      contractId: null,
      hireDate: null,
      isWorkingCurrently: true,
      birthday: null,
    },
    validate: {
      name: validateRequired,
      phone: combineValidators(
        validateRequired,
        validatePhoneNumberStartsWithZero,
        validatePhoneNumberIsNumeric,
        validateNoSpaces,
        validateMinLength(11)
      ),
      contractId: validateRequired,
      hireDate: validateRequired,
      birthday: validateRequired,
    },
  });

  useEffect(() => {
    if (applicant) {
      form.setValues({
        name: applicant.name || "",
        phone: denormalizePhone(applicant.phone || ""),
        departmentIds: applicant.departments?.map((d: any) => d.value) || [],
        // Adayın rol + yetenekleri tek etiket listesinde birleşir.
        tagIds: [
          ...(applicant.tags?.map((t: any) => t.value) || []),
        ],
          contractId: applicant.contract?.value || "",
        hireDate: undefined,
        birthday: applicant.birthday ? new Date(applicant.birthday) : undefined,
        isWorkingCurrently: true,
      });
    }
  }, [applicant]);

  const handleDocumentChange = (
    index: number,
    key: "file" | "name",
    value: any
  ) => {
    const updated = [...documents];
    updated[index][key] = value;
    setDocuments(updated);
  };

  const handleSubmit = async (values: CreateEmployeeParams) => {
    try {
      const hireDate = values.hireDate ? new Date(values.hireDate) : undefined;
      if (hireDate) hireDate.setUTCHours(0, 0, 0, 0);

      const birthday = values.birthday ? new Date(values.birthday) : undefined;
      if (birthday) birthday.setUTCHours(0, 0, 0, 0);

      const result = unwrap(
        await createEmployee({
          ...values,
          hireDate: hireDate?.toISOString(),
          birthday: birthday?.toISOString(),
          isWorkingCurrently: true,
        })
      );
      const data: any = result.data;

      if (result.ok && data?.employeeId) {
        const filesToUpload = documents?.filter((doc: any) => doc.file);

        if (filesToUpload && filesToUpload.length > 0) {
          const uploadPromises = filesToUpload.map((doc: any) =>
            uploadEmployeeDocument({
              file: doc.file!,
              employeeId: data.employeeId,
              name: doc?.name,
              fileId: doc.id,
            })
          );

          await Promise.all(uploadPromises);
        }

        creationNotification("Çalışan", true);
        form.reset();
        return;
      }

      if (data?.error === "This phone number is being used") {
        samePhoneNumberErroNotif();
        return;
      }
      errorNotification({ message: result.message ?? undefined });
    } catch (error) {
      console.error("Error:", error);
      errorNotification();
    }
  };

  return (
    <Container>
      <PageHeader
        pageTitle="Yeni Çalışan Kaydı"
        isButtoned
        buttonText="Oluştur"
        buttonIcon={<PlusIcon />}
        buttonOnClick={() => formRef.current?.requestSubmit()}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "40px" }}>
        <form
          ref={formRef}
          style={{ display: "flex", flexDirection: "column", gap: "24px" }}
          onSubmit={form.onSubmit(handleSubmit)}
        >
          <FormRow $gap={50}>
            <FormColumn>
              <TextInput
                label="Ad Soyad"
                placeholder="Ad Soyad Giriniz."
                radius={10}
                maxLength={40}
                w={"100%"}
                {...form.getInputProps("name")}
                withAsterisk
              />
              <TextInput
                label="Telefon"
                placeholder="05*********"
                radius={10}
                type="tel"
                w={"100%"}
                maxLength={11}
                {...form.getInputProps("phone")}
                withAsterisk
              />
              <CustomDatePicker
                label="İşe Giriş Tarihi"
                placeholder="Tarih seçiniz."
                w="100%"
                {...form.getInputProps("hireDate")}
                withAsterisk
              />
              <CustomDatePicker
                label="Doğum Tarihi"
                placeholder="Tarih seçiniz."
                w="100%"
                {...form.getInputProps("birthday")}
                withAsterisk
              />
            </FormColumn>
            <FormColumn>
              <EmployeeTagFields form={form} />
            </FormColumn>
          </FormRow>

          {company?.modules?.isDocumentUploadAvailable && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "15px",
              }}
            >
              {documents?.map((doc: any, index: any) => (
                <DocumentUploadCard
                  key={index}
                  file={doc?.file}
                  name={doc?.name}
                  setFile={(file: any) =>
                    handleDocumentChange(index, "file", file)
                  }
                  setName={(name: any) =>
                    handleDocumentChange(index, "name", name)
                  }
                  isRequiredDoc={true}
                />
              ))}
            </div>
          )}
        </form>
      </div>
    </Container>
  );
};

export default NewEmployee;
